<script lang="ts" setup>
    import { AlertHandler } from "@byloth/vuert";

    import AppButton from "@/components/ui/AppButton.vue";
    import FeatherIcon from "@/components/ui/FeatherIcon.vue";
</script>

<template>
    <AlertHandler v-slot="{ alert, customComponent, isOpen, resolve, reject }" class="modals-handler">
        <Transition appear
                    name="fade"
                    mode="out-in">
            <div v-if="alert"
                 v-show="isOpen"
                 class="modal-dialog"
                 :class="`modal-dialog--${alert.type}`">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 v-if="alert.title" class="modal-title">
                            <FeatherIcon v-if="alert.icon"
                                         class="modal-icon"
                                         :icon="alert.icon" />
                            {{ alert.title }}
                        </h5>
                        <h5 v-else-if="alert.icon" class="modal-title">
                            <FeatherIcon class="modal-icon" :icon="alert.icon" />
                        </h5>
                        <FeatherIcon v-if="alert.dismissible"
                                     aria-label="Close"
                                     class="modal-close"
                                     icon="x"
                                     @click="resolve()" />
                    </div>
                    <div class="modal-body">
                        <template v-if="customComponent">
                            <Component :is="customComponent"
                                       :resolve="resolve"
                                       :reject="reject" />
                        </template>
                        <pre v-else>{{ alert.message }}</pre>
                    </div>
                    <div v-if="alert.actions?.length" class="modal-footer">
                        <AppButton v-for="action in alert.actions"
                                   :key="action.id"
                                   :theme="action.type"
                                   small
                                   @click="resolve(action)">
                            {{ action.label }}
                        </AppButton>
                    </div>
                </div>
            </div>
        </Transition>
    </AlertHandler>
</template>

<style lang="scss" scoped>
    .modals-handler
    {
        bottom: 0px;
        left: 0px;
        right: 0px;
        padding: 1.5rem;
        pointer-events: none;
        position: fixed;

        & > .modal-dialog
        {
            background-color: #FFFFFF;
            border: 1px solid #E9D9FF;
            border-radius: 1rem;
            margin: auto;
            padding: 0.75rem 1.5rem;
            width: fit-content;

            & > .modal-content
            {
                & > .modal-header
                {
                    margin-bottom: 0.5rem;

                    & > .modal-title
                    {
                        flex: 1;

                        & > .modal-icon
                        {
                            margin-right: 0.125em;
                            vertical-align: -2.5px;
                        }
                    }
                    & > .modal-close
                    {
                        cursor: pointer;
                        font-size: 1.333rem;
                        opacity: 0.5;
                        transition: opacity 150ms ease-in-out;

                        &:hover
                        {
                            opacity: 1;
                        }
                    }
                }
                & > .modal-body > pre
                {
                    font-family: inherit;
                    margin: 0px;
                    white-space: pre-wrap;
                }
                & > .modal-footer
                {
                    display: flex;
                    flex-direction: row-reverse;
                    justify-content: flex-start;
                    margin-top: 1rem;
                    gap: 0.5rem;
                }
            }

            &.modal-dialog--
            {
                &info
                {
                    background-color: #F8F5FF;
                    border-color: #D6BBFB;
                    color: #7B4BFF;

                    .btn-primary
                    {
                        background-color: #7B4BFF;
                        border-color: #7B4BFF;

                        &:hover
                        {
                            background-color: #693FE6;
                            border-color: #693FE6;
                        }
                    }
                    .btn-secondary
                    {
                        background-color: #EDEBFF;
                        border-color: #D6BBFB;
                        color: #7B4BFF;

                        &:hover
                        {
                            background-color: #D6BBFB;
                            border-color: #C0A1F7;
                            color: #7B4BFF;
                        }
                    }
                }
                &success
                {
                    background-color: #F1FAF5;
                    border-color: #6EE7B7;
                    color: #00B74A;

                    .btn-primary
                    {
                        background-color: #00B74A;
                        border-color: #00B74A;

                        &:hover
                        {
                            background-color: #009F40;
                            border-color: #009F40;
                        }
                    }
                    .btn-secondary
                    {
                        background-color: #E6F7ED;
                        border-color: #6EE7B7;
                        color: #00B74A;

                        &:hover
                        {
                            background-color: #6EE7B7;
                            border-color: #4DD9A4;
                            color: #00B74A;
                        }
                    }
                }
                &warning
                {
                    background-color: #FFFDF5;
                    border-color: #FFE9A7;
                    color: #FF8D07;

                    .btn-primary
                    {
                        background-color: #FF8D07;
                        border-color: #FF8D07;

                        &:hover
                        {
                            background-color: #E67D00;
                            border-color: #E67D00;
                        }
                    }
                    .btn-secondary
                    {
                        background-color: #FFF7E6;
                        border-color: #FFE9A7;
                        color: #FF8D07;

                        &:hover
                        {
                            background-color: #FFE9A7;
                            border-color: #FFDC7A;
                            color: #FF8D07;
                        }
                    }
                }
                &error
                {
                    background-color: #FFF4F1;
                    border-color: #FFC1B7;
                    color: #D92700;

                    .btn-primary
                    {
                        background-color: #D92700;
                        border-color: #D92700;

                        &:hover
                        {
                            background-color: #BF2200;
                            border-color: #BF2200;
                        }
                    }
                    .btn-secondary
                    {
                        background-color: #FFEBE6;
                        border-color: #FFC1B7;
                        color: #D92700;

                        &:hover
                        {
                            background-color: #FFC1B7;
                            border-color: #FFA799;
                            color: #D92700;
                        }
                    }
                }
                &question
                {
                    background-color: #F5F8FF;
                    border-color: #BBD4FF;
                    color: #3F8AFF;

                    .btn-primary
                    {
                        background-color: #3F8AFF;
                        border-color: #3F8AFF;

                        &:hover
                        {
                            background-color: #2B7AE6;
                            border-color: #2B7AE6;
                        }
                    }
                    .btn-secondary
                    {
                        background-color: #E6F0FF;
                        border-color: #BBD4FF;
                        color: #3F8AFF;

                        &:hover
                        {
                            background-color: #BBD4FF;
                            border-color: #99C0FF;
                            color: #3F8AFF;
                        }
                    }
                }
            }

            &.fade-enter-from,
            &.fade-leave-to
            {
                opacity: 0;

                & > .modal-dialog
                {
                    transform: translateY(-2.5em);
                }
            }
            &.fade-enter-active,
            &.fade-leave-active
            {
                transition: opacity 150ms ease-in-out;

                & > .modal-dialog
                {
                    transition: transform 150ms ease-in-out;
                }
            }
            &.fade-enter-to,
            &.fade-leave-from
            {
                opacity: 1;

                & > .modal-dialog
                {
                    transform: translateY(0px);
                }
            }
        }
    }
</style>
